// Keeps this page's theme in step with the login's (GET/PUT
// /api/account/appearance), so a theme picked on the phone shows on the web
// and in the Mac app without a reload, and the other way round. The rule
// itself is lib/appearanceSync.ts; this is when it runs:
//   - at sign-in, on every return to the window (focus, or the tab shown
//     again; at most every 10 s) and every 5 minutes;
//   - at once after a choice: any theme change in this page the sync did
//     not make itself (Settings → Appearance, the companion's More screen,
//     the UI guide). The choice is remembered until the server has it.
// The local theme still paints first (index.html's inline script, the ui
// store); a failure is silent and tried again next time.
//
// Off inside the iPhone app's web screens: the app owns the theme there,
// applies the same rule natively and hands its theme to each page.

import { useEffect } from "react";

import { getAppearance, setAppearance, type AppearanceTheme } from "@/api/appearance";
import { pendingChoice, planAppearance, rememberChoice, settleChoice } from "@/lib/appearanceSync";
import { DEFAULT_THEME, THEME_NAMES, useUi } from "@/stores/ui";
import type { ThemeName } from "@/theme/themes";

const REREAD_MS = 5 * 60_000;
const FOCUS_GAP_MS = 10_000;

// Module state: one page has one sync, whatever re-renders.
let applying = false; // the sync is setting the theme: not a choice
let choices = 0; // bumped by every choice; a read that started before one is dropped
let running = false;
let again = false;
let lastRead = 0;
let currentUser: string | null = null; // whose choices are sent (a sign-in in this page changes it)

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

async function syncOnce(user: string): Promise<void> {
  const seen = choices;
  let server: string | null;
  try {
    server = (await getAppearance()).theme ?? null;
  } catch {
    return; // offline, signed out or an older server: keep what is shown
  }
  lastRead = Date.now();
  if (seen !== choices) {
    again = true; // a choice came in while reading: plan again with it
    return;
  }
  const store = storage();
  const pending = pendingChoice(store, user);
  const plan = planAppearance({
    local: useUi.getState().theme,
    server,
    pending,
    known: THEME_NAMES,
    fallback: DEFAULT_THEME,
  });
  if (plan.clearPending && pending) settleChoice(store, user, pending);
  if (plan.apply) {
    applying = true;
    try {
      useUi.getState().setTheme(plan.apply as ThemeName);
    } finally {
      applying = false;
    }
  }
  if (plan.send) {
    try {
      await setAppearance(plan.send as AppearanceTheme);
      settleChoice(store, user, plan.send);
    } catch {
      // Still pending (if it was a choice): sent again at the next sync.
    }
  }
}

/** One sync at a time; a call during one runs another right after it. */
async function sync(): Promise<void> {
  if (running) {
    again = true;
    return;
  }
  running = true;
  try {
    do {
      again = false;
      if (currentUser) await syncOnce(currentUser);
    } while (again);
  } finally {
    running = false;
  }
}

/** `user` is the signed-in login (null when signed out); `enabled` false turns the sync off. */
export function useAppearanceSync(user: string | null, enabled: boolean): void {
  useEffect(() => {
    if (!enabled || !user) return;
    currentUser = user;

    const unsubscribe = useUi.subscribe((state, prev) => {
      if (state.theme === prev.theme || applying) return;
      choices += 1;
      rememberChoice(storage(), user, state.theme);
      void sync();
    });
    const reread = () => {
      if (Date.now() - lastRead >= FOCUS_GAP_MS) void sync();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") reread();
    };
    window.addEventListener("focus", reread);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => void sync(), REREAD_MS);
    void sync();

    return () => {
      unsubscribe();
      window.removeEventListener("focus", reread);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
      if (currentUser === user) currentUser = null;
    };
  }, [user, enabled]);
}
